// Campaign performance report tabs (/thanx_campaigns/performance), gated by
// the mx-promote-insights-lab-reports flag:
//
// - Flag off (default): the pre-existing shape, unchanged from master. Both
//   tabs render unconditionally here; the separate, pre-existing
//   mx-email-engagement-by-campaign-looker-report flag is consulted one
//   level up, in index.tsx, which chooses whether to render this whole
//   component (both tabs) or just the bare overview content with no tab
//   bar at all — this component itself has no awareness of that flag.
//
// - Flag on: the Notion-specified 3-tab shape, verified against the
//   production Looker report catalog:
//     1. Redemption revenue (dashboards/359) - 1st/default tab.
//     2. Campaign email engagement (dashboards/110) - reuses the same
//        EmailEngagementOverview content as the flag-off tab, labeled
//        differently per Notion.
//     3. Email engagement by campaign type (dashboards/274) - a *different*
//        dashboard than the flag-off tab of the same name (dashboards/269).
//        Dashboards/269 has zero rows in the production Looker report
//        catalog; 274 is the catalog's real promoted report for this tab
//        name, so this state renders a separate, plain LookerEmbedTab
//        pointed at 274 instead of reusing the flag-off tab's markup.
import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import ScrollingTabNav from 'components/Tabs/ScrollingTabNav'
import TabItem from 'components/Tabs/TabItem'
import TabNav from 'components/Tabs/TabNav'
import TabPane from 'components/Tabs/TabPane'
import { buildTranslate } from 'locales'
import { Fields as Merchant } from 'models/Merchant'
import { Container } from 'react-bootstrap-five'
import Tab from 'react-bootstrap-five/Tab'
import LookerFooter from 'scenes/LookerEmbed/components/LookerFooter'
import LookerIframe from 'scenes/LookerEmbed/LookerIframe'

const t = buildTranslate('thanx_campaigns.performance')
const tError = buildTranslate('errors')
const tLooker = buildTranslate('looker')

export enum Tabs {
  REDEMPTION_REVENUE = 'redemption_revenue',
  EMAIL_ENGAGEMENT_OVERVIEW = 'email_engagement_overview',
  EMAIL_ENGAGEMENT_BY_CAMPAIGN_TYPE = 'email_engagement_by_campaign_type',
}

interface CampaignPerformanceTabsProps {
  activeTab: Tabs
  setActiveTab: (tab: Tabs) => void
  // When true, renders the Notion-specified 3-tab shape described above.
  // When false, renders master's original shape (see file header).
  isPromoteInsightsLabReportsEnabled: boolean
  redemptionRevenueContent: React.ReactNode
  emailEngagementOverviewContent: React.ReactNode
  emailEngagementByCampaignTypeContent: React.ReactNode
  userIsAdmin: boolean
  merchant: Merchant
  emailEngagementByCampaignTypePath: string
  emailEngagementByCampaignTypeHeight: number
}

const CampaignPerformanceTabs = ({
  activeTab,
  setActiveTab,
  isPromoteInsightsLabReportsEnabled,
  redemptionRevenueContent,
  emailEngagementOverviewContent,
  emailEngagementByCampaignTypeContent,
  userIsAdmin,
  merchant,
  emailEngagementByCampaignTypePath,
  emailEngagementByCampaignTypeHeight,
}: CampaignPerformanceTabsProps) => {
  const [css] = useStyletron()

  // Non-admin merchant users must never reach a Looker embed. The flag-off tabs
  // each gate on userIsAdmin, but the promoted flag-on tabs render
  // LookerEmbedTab, which carries no gate of its own — and the backend's
  // GET /looker/embed_url only checks the looker-embed-paths allowlist, never
  // the caller's role. So this is the only gate, and both flag states have to
  // apply it for their behaviour to stay identical.
  const forbiddenFallback = (
    <Text.BodyText3 className="mb-xl">
      {tError('forbidden_title')}
    </Text.BodyText3>
  )

  return (
    <Tab.Container
      activeKey={activeTab}
      transition={false}
      onSelect={(tab: string | null) => {
        if (!tab) return
        setActiveTab(tab as Tabs)
      }}
    >
      {/* The nav component itself is branched on the flag, not just its
          items: the promoted row carries enough tabs to wrap onto a second
          line at common widths, which is what ScrollingTabNav exists for,
          while the flag-off row must stay byte-identical to master — so that
          branch keeps TabNav. */}
      {isPromoteInsightsLabReportsEnabled ? (
        <ScrollingTabNav ariaLabel={t('pageTitle')}>
          <TabItem
            state={Tabs.REDEMPTION_REVENUE}
            title={t('redemptionRevenue.title')}
          />
          <TabItem
            state={Tabs.EMAIL_ENGAGEMENT_OVERVIEW}
            title={t('campaignEmailEngagement.title')}
          />
          <TabItem
            state={Tabs.EMAIL_ENGAGEMENT_BY_CAMPAIGN_TYPE}
            title={t('emailEngagementByCampaignType.title')}
          />
        </ScrollingTabNav>
      ) : (
        <TabNav>
          <TabItem
            state={Tabs.EMAIL_ENGAGEMENT_OVERVIEW}
            title={tLooker(
              `urls.dashboards/269.tabs.email_engagement_overview`
            )}
          />
          <TabItem
            state={Tabs.EMAIL_ENGAGEMENT_BY_CAMPAIGN_TYPE}
            title={tLooker(
              `urls.dashboards/269.tabs.email_engagement_by_campaign_type`
            )}
          />
        </TabNav>
      )}
      <Tab.Content className="pb-l h-100 grey-05-bkg">
        {isPromoteInsightsLabReportsEnabled && (
          <TabPane state={Tabs.REDEMPTION_REVENUE} mountOnEnter>
            <Switch condition={userIsAdmin} fallback={forbiddenFallback}>
              {redemptionRevenueContent}
            </Switch>
          </TabPane>
        )}
        {/* mountOnEnter is a no-op with the flag off, where this is the
            default tab and mounts as the active pane anyway — so the
            flag-off render stays byte-identical to master. With the flag on
            the default moves to Redemption revenue, and without this the
            pane's Looker embed would still fetch and mount behind it. */}
        <TabPane state={Tabs.EMAIL_ENGAGEMENT_OVERVIEW} mountOnEnter>
          {emailEngagementOverviewContent}
        </TabPane>
        {isPromoteInsightsLabReportsEnabled ? (
          <TabPane state={Tabs.EMAIL_ENGAGEMENT_BY_CAMPAIGN_TYPE} mountOnEnter>
            <Switch condition={userIsAdmin} fallback={forbiddenFallback}>
              {emailEngagementByCampaignTypeContent}
            </Switch>
          </TabPane>
        ) : (
          <TabPane state={Tabs.EMAIL_ENGAGEMENT_BY_CAMPAIGN_TYPE}>
            <div className="pb-l grey-05-bkg">
              <div className="container pl-0 pr-0">
                <Container
                  className={`px-0 ${css({
                    minHeight: `${emailEngagementByCampaignTypeHeight + 300}px`,
                  })}`}
                >
                  <Switch condition={userIsAdmin} fallback={forbiddenFallback}>
                    <LookerIframe
                      path={emailEngagementByCampaignTypePath}
                      merchant={merchant}
                      height={`${emailEngagementByCampaignTypeHeight}px`}
                      title={t('emailEngagementByCampaignType.title')}
                    />
                    <LookerFooter className="my-xl" />
                  </Switch>
                </Container>
              </div>
            </div>
          </TabPane>
        )}
      </Tab.Content>
    </Tab.Container>
  )
}

export default CampaignPerformanceTabs
